import { useState, useEffect, useCallback, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { marketingApi } from '../api/marketing'
import Boton from '../components/Boton'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import { fmtFecha, fmtHoraDe, fmtMonto } from '../utils/fecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'

/**
 * MARKETING → Próxima Selección (Martin, 25 sep 2026): cada cliente que, al
 * comprar, eligió que lo contactemos en N días — con toda su trazabilidad:
 * pedido → programado → selección armada → mail → abierto → entró → compró.
 * Solo lectura; la regla vive en ProximaSeleccionController.
 */

const VISTAS = [
  { id: '', label: 'Todos' },
  { id: 'programados', label: 'Programados' },
  { id: 'enviados', label: 'Enviados' },
  { id: 'compraron', label: 'Compraron' },
  { id: 'bajas', label: 'Bajas y fallas' },
]

const ETAPA = {
  programado: { label: 'Programado', bg: colors.warnBg, fg: colors.warn },
  enviado: { label: 'Enviado', bg: colors.infoBg, fg: colors.info },
  compro: { label: 'Compró', bg: colors.okBg, fg: colors.ok },
  baja: { label: 'Baja', bg: colors.bgSoft, fg: colors.textSub },
  fallo: { label: 'Falló', bg: colors.errBg, fg: colors.err },
}

const CANAL = { storefront: 'Web', magento: 'Magento', lgr: 'LGR', movil: 'Móvil' }
const plata = (n) => (n == null ? '—' : `$ ${fmtMonto(n, 0)}`)
const hh = (h) => `${String(h).padStart(2, '0')}:00`

export default function MktProximaSeleccionPage() {
  const [filtros, setFiltros] = useStoredState('lgr.filtros.proxima-seleccion', { vista: '', q: '' })
  const [q, setQ] = useState(filtros.q)
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const [abierto, setAbierto] = useState(null)

  const cargar = useCallback(async () => {
    try {
      const r = await marketingApi.proximaSeleccion({ vista: filtros.vista || undefined, q: filtros.q || undefined })
      setData(r.data)
      setError(false)
    } catch { setError(true) }
  }, [filtros])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(cargar, 30000)

  const filas = data?.data ?? []
  const k = data?.kpis

  return (
    <div>
      <div style={st.header}>
        <div>
          <h2 style={st.title}>Próxima Selección</h2>
          <p style={st.sub}>
            Clientes que, al comprar, pidieron que les preparemos su próxima selección en N días.
            El mail sale el día elegido, a la hora de su última compra.
            {k?.internos > 0 && ` Las casillas internas (${k.internos}) se listan pero no cuentan en los números.`}
          </p>
        </div>
      </div>

      <div style={st.kpis}>
        <Kpi label="Pedidos" valor={k?.pedidos} />
        <Kpi label="Programados" valor={k?.programados} />
        <Kpi label="Enviados" valor={k?.enviados} />
        <Kpi label="Abrieron" valor={k?.abiertos} base={k?.enviados} title="Abrieron el mail" />
        <Kpi label="Entraron" valor={k?.entraron} base={k?.enviados} title="Entraron a la página de su selección" />
        <Kpi label="Por el link" valor={k?.compraron_link} base={k?.enviados} destacado title="Compraron desde el link del mail" />
        <Kpi label="Volvieron" valor={k?.volvieron} base={k?.enviados} destacado
          title={`Volvieron a comprar por cualquier canal dentro de los ${data?.dias_recompra ?? 30} días posteriores al mail (sin contar la compra por el link)`} />
        <Kpi label="Bajas" valor={k?.bajas} />
      </div>

      <div style={sx.filterBar}>
        {VISTAS.map(v => (
          <button key={v.id} type="button" onClick={() => setFiltros({ ...filtros, vista: v.id })}
            style={{ ...sx.pill, ...(filtros.vista === v.id ? sx.pillActive : {}) }}>{v.label}</button>
        ))}
        <form onSubmit={e => { e.preventDefault(); setFiltros({ ...filtros, q: q.trim() }) }} style={st.buscar}>
          <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Cliente, email o nº de pedido…" style={{ ...sx.input, flex: 1 }} />
          <Boton type="submit" variante="secondary">Buscar</Boton>
          {(filtros.q || filtros.vista) && <Boton type="button" variante="ghost" onClick={() => { setQ(''); setFiltros({ vista: '', q: '' }) }}>Limpiar</Boton>}
        </form>
      </div>

      {error && <p style={st.error}>No se pudo actualizar el listado. Se reintenta solo en unos segundos.</p>}

      <div style={st.tableWrap}>
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Pedido el</th>
              <th style={sx.th}>Cliente</th>
              <th style={sx.th}>En la compra</th>
              <th style={sx.th}>Contactar</th>
              <th style={sx.th}>Estado</th>
              <th style={sx.th}>Resultado</th>
            </tr>
          </thead>
          <tbody>
            {!data && !error && <tr><td colSpan={6} style={st.vacio}>Cargando…</td></tr>}
            {data && filas.length === 0 && <tr><td colSpan={6} style={st.vacio}>No hay pedidos para este filtro.</td></tr>}
            {filas.map(f => (
              <Fragment key={f.id}>
                <tr style={{ ...st.fila, ...(abierto === f.id ? st.filaAbierta : {}) }}
                  onClick={() => setAbierto(abierto === f.id ? null : f.id)} title="Ver el recorrido completo"
                  onMouseEnter={e => { e.currentTarget.style.background = colors.bgSoft }}
                  onMouseLeave={e => { e.currentTarget.style.background = abierto === f.id ? colors.bgSoft : 'transparent' }}>
                  <td style={st.td}><Fecha iso={f.pedido_at} /></td>
                  <td style={st.td} title={f.cliente.codigo ? `Cliente ${f.cliente.codigo}` : undefined}>
                    <div style={st.nombre}>{f.cliente.nombre || 'Sin nombre en la ficha'}{f.interno && <span style={st.interno} title="Casilla interna: no cuenta en las estadísticas">interno</span>}</div>
                    <div style={st.subLinea}>{f.contacto}</div>
                    {f.canal && f.canal !== 'email' && <div style={st.subLinea}>{f.canal === 'ambos' ? 'Mail y WhatsApp' : 'WhatsApp'}{f.telefono ? ` · ${f.telefono}` : ''}</div>}
                  </td>
                  <td style={st.td}>
                    {f.origen ? <>
                      <Link to={`/notas-venta/${f.origen.id}`} onClick={e => e.stopPropagation()} style={st.link}>{f.origen.numero}</Link>
                      <div style={st.subLinea}>{CANAL[f.origen.canal] ?? f.origen.canal} · <span style={sx.mono}>{plata(f.origen.total)}</span></div>
                    </> : '—'}
                  </td>
                  <td style={st.td}>
                    <div style={sx.mono}>{fmtFecha(f.programado_para)}{f.hora_envio != null && <span style={st.hora}>{hh(f.hora_envio)}</span>}</div>
                    <div style={st.subLinea}>{f.dias_elegidos != null ? `cada ${f.dias_elegidos} días${f.ciclo > 1 ? ` · envío ${f.ciclo}` : ''}` : ''}</div>
                  </td>
                  <td style={st.td}><Badge etapa={f.etapa} /><Recorrido f={f} /></td>
                  <td style={st.td}><Resultado f={f} /></td>
                </tr>
                {abierto === f.id && (
                  <tr><td colSpan={6} style={st.detalleCelda}><Detalle f={f} /></td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Kpi({ label, valor, base, destacado, title }) {
  const pct = base ? `${Math.round((valor || 0) / base * 100)} %` : ''
  return (
    <div style={{ ...st.kpi, ...(destacado ? st.kpiDestacado : {}) }} title={title}>
      <div style={st.kpiLabel}>{label}</div>
      <div style={{ ...st.kpiValor, color: destacado ? colors.ok : colors.text }}>
        {valor ?? '—'}<span style={st.kpiPct}>{valor != null ? pct : ''}</span>
      </div>
    </div>
  )
}

function Fecha({ iso }) {
  if (!iso) return <span style={st.nada}>—</span>
  return <span style={sx.mono}>{fmtFecha(iso)}<span style={st.hora}>{fmtHoraDe(iso)}</span></span>
}

function Badge({ etapa }) {
  const e = ETAPA[etapa] ?? ETAPA.programado
  return <span style={{ ...sx.badge, background: e.bg, color: e.fg }}>{e.label}</span>
}

/**
 * Debajo del estado, los pasos de cada canal. Mail: salió · lo abrió · entró.
 * WhatsApp (4 oct 2026): WA 1 · lo leyó · respondió · le llegó la selección · entró.
 */
function Recorrido({ f }) {
  if (!f.enviado_at) return null
  const paso = (hecho, label) => (
    <span style={{ ...st.paso, color: hecho ? colors.ok : colors.textMeta }}>{hecho ? '✓' : '○'} {label}</span>
  )
  const w = f.wa
  return <>
    {f.mail_enviado_at && <span style={st.pasos}>{paso(true, 'Mail')}{paso(!!f.abierto_at, 'Abrió')}{paso(!!f.click_at, 'Entró')}</span>}
    {w?.wa1_at && <span style={st.pasos}>{paso(true, 'WA')}{paso(!!w.wa1_leido_at, 'Leyó')}{paso(!!w.respondio_at, 'Respondió')}{paso(!!w.wa2_at, 'Selección')}{paso(!!w.click_at, 'Entró')}</span>}
    {w?.error && !w.wa1_at && <span style={{ ...st.pasos, color: colors.warn }} title={w.error}>WhatsApp no salió ▾</span>}
  </>
}

function Resultado({ f }) {
  // Canal y fecha de la recompra van en el recorrido (detalle).
  const compra = (label, nv) => (
    <div style={st.nowrap}>
      <div style={{ ...st.resLabel, color: colors.ok }}>{label} · <span style={sx.mono}>{plata(nv.total)}</span></div>
      <Link to={`/notas-venta/${nv.id}`} onClick={e => e.stopPropagation()} style={{ ...st.link, fontSize: text.sec }}>{nv.numero}</Link>
    </div>
  )
  if (f.compra_link) return compra('✓ Por el link', f.compra_link)
  if (f.volvio_a_comprar) return compra('✓ Volvió a comprar', f.volvio_a_comprar)
  if (f.etapa === 'baja') return <span style={st.subLinea}>Pidió la baja</span>
  if (f.etapa === 'fallo') return <span style={{ ...st.subLinea, color: colors.err }}>Ver el motivo ▾</span>
  return <span style={st.nada}>—</span>
}

function Detalle({ f }) {
  const hitos = [
    ['Pidió que lo contactemos', f.pedido_at, f.origen ? <>en la compra <span style={sx.mono}>{f.origen.numero}</span>, cada {f.dias_elegidos} días{f.ciclo > 1 ? ` (este es el envío ${f.ciclo} del ciclo)` : ''}</> : null],
    ['Selección armada', f.generado_at, f.generado_con ? (f.generado_con === 'ia' ? 'la eligió la IA' : 'por regla (sin IA)') : null],
    ...(f.canal !== 'whatsapp' || f.mail_enviado_at ? [
      ['Mail enviado', f.mail_enviado_at, f.contacto],
      ['Abrió el mail', f.abierto_at, null],
    ] : []),
    ...(f.wa ? [
      ['WhatsApp 1 (recordatorio)', f.wa.wa1_at, f.telefono],
      ['Leyó el WhatsApp', f.wa.wa1_leido_at, null],
      ['Respondió', f.wa.respondio_at, null],
      ['WhatsApp 2 (su selección)', f.wa.wa2_at, f.wa.wa2_via === 'plantilla' ? 'no respondió en 24 h: salió con el link' : f.wa.wa2_via === 'sesion' ? 'al instante, con el link' : null],
      ['Entró desde el WhatsApp', f.wa.click_at, null],
    ] : []),
    ['Entró a su selección', f.click_at, null],
    ['Compró por el link', f.compra_link?.fecha, f.compra_link ? <span style={sx.mono}>{f.compra_link.numero}</span> : null],
    ['Volvió a comprar', f.volvio_a_comprar?.fecha, f.volvio_a_comprar ? <><span style={sx.mono}>{f.volvio_a_comprar.numero}</span> · {CANAL[f.volvio_a_comprar.canal] ?? f.volvio_a_comprar.canal}</> : null],
  ]
  return (
    <div style={st.detalle}>
      <div>
        <h3 style={sx.cardTitle}>Recorrido</h3>
        <ol style={st.hitos}>
          {hitos.map(([label, iso, nota]) => (
            <li key={label} style={{ ...st.hito, color: iso ? colors.text : colors.textMeta }}>
              <span style={{ ...st.punto, background: iso ? colors.ok : colors.border }} />
              <span style={st.hitoLabel}>{label}</span>
              <span style={st.hitoFecha}>{iso ? <Fecha iso={iso} /> : ((label === 'Mail enviado' || label === 'WhatsApp 1 (recordatorio)') && f.etapa === 'programado' ? <>programado <span style={sx.mono}>{fmtFecha(f.programado_para)}{f.hora_envio != null && <span style={st.hora}>{hh(f.hora_envio)}</span>}</span></> : '—')}</span>
              {iso && nota && <span style={st.hitoNota}>{nota}</span>}
            </li>
          ))}
        </ol>
        {f.motivo && <p style={st.motivo}>{f.motivo}</p>}
        {f.wa?.error && <p style={st.motivo}>WhatsApp: {f.wa.error}</p>}
        {f.wa?.conversacion_id && <Link to={`/whatsapp/${f.wa.conversacion_id}`} style={st.link}>Ver la conversación de WhatsApp →</Link>}
      </div>
      <div>
        <h3 style={sx.cardTitle}>Selección propuesta</h3>
        {f.items.length === 0
          ? <p style={st.nada}>Se arma el día elegido, con stock y precios de ese día.</p>
          : <table style={{ ...sx.table, marginTop: space.sm }}>
            <tbody>
              {f.items.map(i => (
                <tr key={i.sku} style={st.itemFila}>
                  <td style={st.itemTd}><span style={sx.mono}>{i.cantidad}×</span></td>
                  <td style={st.itemTd}>{i.nombre}<div style={st.subLinea}><span style={sx.mono}>{i.sku}</span>{i.origen === 'original' ? ' · lo que ya compraba' : i.origen ? ' · recomendado' : ''}</div></td>
                  <td style={{ ...st.itemTd, textAlign: 'right' }}><span style={sx.mono}>{plata(i.precio * i.cantidad)}</span></td>
                </tr>
              ))}
              <tr>
                <td style={st.itemTd} />
                <td style={{ ...st.itemTd, color: colors.textSec }}>Subtotal</td>
                <td style={{ ...st.itemTd, textAlign: 'right', fontWeight: 600 }}><span style={sx.mono}>{plata(f.subtotal)}</span></td>
              </tr>
            </tbody>
          </table>}
      </div>
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.lg },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: `${space.xs}px 0 0`, fontSize: text.sec, color: colors.textSec },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: space.md, marginBottom: space.lg },
  kpi: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px` },
  kpiDestacado: { borderColor: colors.ok, background: colors.okSoft },
  kpiLabel: { fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  kpiValor: { ...sx.mono, fontSize: 20, fontWeight: 600, marginTop: space.xs },
  kpiPct: { fontFamily: font.sans, fontSize: text.sec, fontWeight: 400, color: colors.textSec, marginLeft: space.sm },
  buscar: { display: 'flex', gap: space.md, alignItems: 'center', marginLeft: 'auto', flex: '1 1 320px', maxWidth: 480 },
  error: { fontSize: text.sec, color: colors.err, margin: `0 0 ${space.md}px` },
  tableWrap: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflowX: 'auto' },
  fila: { height: 52, borderBottom: `1px solid ${colors.borderSoft}`, cursor: 'pointer' },
  filaAbierta: { background: colors.bgSoft },
  vacio: { ...sx.td, height: 64, textAlign: 'center', color: colors.textSec },
  td: { ...sx.td, padding: `0 ${space.lg}px`, whiteSpace: 'nowrap' },
  nowrap: { whiteSpace: 'nowrap' },
  resLabel: { fontSize: text.sec, fontWeight: 600 },
  nombre: { fontWeight: 500 },
  interno: { ...sx.badge, marginLeft: space.sm, background: colors.bgSoft, color: colors.textSec, border: `1px solid ${colors.border}` },
  subLinea: { fontSize: text.sec, color: colors.textMeta, marginTop: 2 },
  hora: { marginLeft: space.sm, color: colors.textMeta },
  link: { ...sx.mono, color: colors.link, textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap' },
  nada: { color: colors.textMeta },
  pasos: { display: 'flex', gap: space.sm, whiteSpace: 'nowrap', marginTop: 2 },
  paso: { fontSize: text.sec, fontWeight: 500 },
  detalleCelda: { padding: 0, background: colors.bgSoft, borderBottom: `1px solid ${colors.border}` },
  detalle: { display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 1.3fr)', gap: space['2xl'], padding: `${space.lg}px ${space.xl}px ${space.xl}px` },
  hitos: { listStyle: 'none', margin: `${space.sm}px 0 0`, padding: 0 },
  hito: { display: 'grid', gridTemplateColumns: '12px 180px 1fr', alignItems: 'center', columnGap: space.md, padding: `${space.xs}px 0`, fontSize: text.body },
  punto: { width: 8, height: 8, borderRadius: radius.pill },
  hitoLabel: { fontWeight: 500 },
  hitoFecha: { fontSize: text.sec },
  hitoNota: { gridColumn: '2 / 4', fontSize: text.sec, color: colors.textSec },
  motivo: { marginTop: space.md, fontSize: text.sec, color: colors.textSec },
  itemFila: { borderBottom: `1px solid ${colors.borderSoft}` },
  itemTd: { padding: `${space.sm}px ${space.md}px`, fontSize: text.body, verticalAlign: 'top' },
}
